<template>
    <div>
        <q-card class="notification" bordered flat>
            <q-expansion-item
                expand-separator
                v-model="triggerExpansion"
                expand-icon-class="text-white"
                style="background-color: red"
            >
                <template v-slot:header>
                    <div class="row full-width">
                        <q-badge
                            v-if="isNew"
                            class="widget-badge"
                            color="red"
                            :label="$t('New')"
                            floating
                            left
                        />
                        <div
                            v-if="eventNotificationDetails"
                            class="row items-center text-white q-ml-xs"
                        >
                            <!-- Icon slightly to the left -->
                            <div
                                class="col-auto flex items-center justify-center"
                                style="margin-left: -4px; margin-right: 12px"
                            >
                                <q-icon
                                    name="fa-regular fa-circle-exclamation"
                                    size="sm"
                                    class="text-white"
                                />
                            </div>

                            <!-- Details on the right -->
                            <div class="col">
                                <div class="row">
                                    <span class="text-h5 text-bold">
                                        {{ eventNotificationDetails.causeName }}
                                    </span>
                                </div>
                                <div class="row">
                                    <span class="text-h6">
                                        {{
                                            getAssetName(
                                                eventNotificationDetails.assetId
                                            )
                                        }}
                                    </span>
                                </div>
                                <div class="row">
                                    <span>
                                        {{ getminutesAgo }} minutes ago
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div v-else class="col-md-12">
                            <span class="text-h6">
                                {{
                                    isLoading
                                        ? $t('Loading notification details...')
                                        : $t('Failed to load notification')
                                }}
                            </span>
                            <!-- Reload banner -->
                            <div v-if="showReloadBanner">
                                <q-btn
                                    @click="initialize"
                                    :loading="isLoading"
                                    :label="$t('Reload')"
                                    color="negative"
                                    size="0.8rem"
                                />
                            </div>
                        </div>
                    </div>
                </template>
                <template v-slot:default>
                    <q-card class="notification">
                        <!-- Reload banner -->
                        <q-card-section v-if="showReloadBanner">
                            <q-banner
                                inline-actions
                                class="text-black bg-white"
                            >
                                {{ $t('Failed to load notification details') }}
                                <template v-slot:action>
                                    <q-btn
                                        @click="initialize"
                                        :loading="isLoading"
                                        :label="$t('Reload')"
                                        color="negative"
                                    />
                                </template>
                            </q-banner>
                        </q-card-section>

                        <!-- Body (loading) -->
                        <q-card-section
                            v-if="!eventNotificationDetails && isLoading"
                            class="text-center"
                        >
                            <q-spinner
                                color="primary"
                                size="3em"
                                :thickness="10"
                            ></q-spinner>
                            <br />
                            <p class="q-mt-md">
                                {{ $t('Loading notification details...') }}
                            </p>
                        </q-card-section>

                        <!-- Body (loaded) -->
                        <q-card-section
                            v-if="eventNotificationDetails"
                            class="q-px-lg q-pt-none"
                        >
                            <div class="q-pt-md">
                                <p style="color: gray">Description</p>
                            </div>
                            <p
                                style="
                                    overflow-wrap: break-word;
                                    white-space: normal;
                                    margin: 0;
                                "
                            >
                                {{
                                    eventNotificationDetails.eventDescription
                                        ? eventNotificationDetails.eventDescription
                                        : $t('No Description Provided')
                                }}
                            </p>
                            <div class="row justify-end">
                                <q-btn
                                    @click="dismiss"
                                    color="white"
                                    text-color="secondary"
                                    outline
                                    v-show="allowDismiss"
                                    size="1.2rem"
                                    :class="{
                                        'full-width q-mt-sm': $q.screen.xs
                                    }"
                                    :label="$t('Dismiss')"
                                />
                            </div>
                            <q-space />
                        </q-card-section>
                    </q-card>
                </template>
            </q-expansion-item>
        </q-card>
    </div>
</template>

<script>
import { getLocaleFormat, getTime } from '@models/timeformats.js';
import { isFeatureEnabled } from '@utils/featureFlags.js';
import { mapState, mapActions } from 'pinia';
import { useAuthStore } from '@stores/auth';
import { useNotificationsStore } from '@stores/notifications.ts';
import { useAssetsStore } from '@stores/assets.ts';

export default {
    name: 'EventAlertNotificationCard',
    props: {
        notification: {
            validator(value) {
                return typeof value === 'object' || value === null;
            },
            required: false
        },
        index: {
            type: Number,
            required: false
        }
    },
    async mounted() {
        await this.initialize();
        this.interval = setInterval(() => {
            this.currentTime = Date.now(); // updates every minute, this is used in minutesAgo computed property
        }, 60000);
    },
    beforeUnmount() {
        // clear the intervals
        clearInterval(this.interval);
    },
    data() {
        return {
            eventNotificationDetails: null,
            currentTime: Date.now(),
            initSuccess: null,
            isLoading: false,
            expand: null
        };
    },
    computed: {
        ...mapState(useAssetsStore, ['getAssetByUUID', 'getAssetName']),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale',
            getUser: 'user'
        }),
        ...mapState(useNotificationsStore, {
            getEventNotificationByEventUUID: 'getEventNotificationByEventUUID'
        }),
        /**
         * Get the received date of the notification.
         * @return {String} - Received date or time.
         */
        receivedAt() {
            if (this.isReceivedToday) {
                return getTime(this.notification['receivedAt']);
            }
            return getLocaleFormat(
                this.notification['receivedAt'],
                this.userLocale
            );
        },
        /**
         * If the notification is new
         * @return {Boolean}
         */
        isNew() {
            return this.notification['isNew'];
        },
        /**
         * Show reload banner.
         * Only show if an attempt to load has been made and there was a failure.
         * @return {Boolean}
         */
        showReloadBanner() {
            if (this.initSuccess === null) {
                return false;
            } else {
                return !this.initSuccess && !this.isLoading;
            }
        },
        /**
         * Allow dismiss notification popup
         */
        allowDismiss() {
            // check if the user is always allowed to dismiss
            if (this.$userCan('always_dismiss', 'event_alert_notifications')) {
                return true;
            }
            // if not, check if the asset allows it
            return this.asset ? this.asset.allow_downtime_dismissal : false;
        },
        /**
         * Open expansion based on index of notification
         * if the notification is at the 0th index then it is at the top of the list
         * and should be open
         */
        triggerExpansion: {
            get() {
                return this.expand === null
                    ? this.index === 0 || this.expand
                    : this.expand;
            },
            set(val) {
                this.expand = val;
                return this.expand;
            }
        },
        /**
         * Check if the notification received date is today.
         * @return {Boolean} - True if received today.
         */
        isReceivedToday() {
            return (
                new Date(this.notification['receivedAt']).toDateString() ===
                new Date().toDateString()
            );
        },
        getminutesAgo() {
            const date = new Date(
                this.eventNotificationDetails.eventStart.replace(' ', 'T')
            );
            return Math.floor((this.currentTime - date.getTime()) / 60000);
        },
        watchNotification() {
            return this.getEventNotificationByEventUUID(
                this.notification.eventUUID
            );
        }
    },
    watch: {
        watchNotification: {
            handler(notification) {
                if (notification?.isResolved) {
                    this.dismiss();
                }
            },
            deep: true,
            immediate: true
        }
    },
    methods: {
        isFeatureEnabled,
        ...mapActions(useNotificationsStore, [
            'dismissEventAlertNotificationAction'
        ]),
        /**
         * Initialize component.
         */
        async initialize() {
            // check if asset details can be fetched from the store
            this.eventNotificationDetails = this.notification;
            this.initSuccess = true;
            this.isLoading = false;
        },
        /**
         * Dismiss downtime notification
         */
        async dismiss() {
            try {
                this.dismissEventAlertNotificationAction(
                    this.notification['eventUUID']
                );
                clearInterval(this.interval);
            } catch (error) {
                console.error('Error dismissing notification due to: ', error);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.notification {
    max-width: 100%;
    border-width: 0.1rem;
    border-color: rgba(0, 0, 0, 0.12);
    font-size: 1rem;
}
.notification-header {
    height: 60px;
}
.event-badge {
    font-size: 16px;
    padding: 4px 6px;
    margin-top: 5px;
}
</style>
